import React, { useEffect, useState } from "react";
import { Button, Col, Input, Row, Select } from "antd";
import AppInput from "../../../../components/AppInput";
import moment from "moment";
import VoyageDetails from "../../../../components/VoyageDetails";
import { post } from "../../../../services/services";
import { ApiURL } from "../../../../services/apiConstants";
import { ErrorToast, SuccessToast } from "../../../../widgets/AppToast";
import { useNavigate, useParams } from "react-router-dom";
import useUserData from "../../../../hooks/useUserData";
import { fuelTypeOptions } from "../../../../Data/data";
import SecurityCodeModal from "../../../../components/SecurityCodeModal";

const { Option } = Select;

const EditBunkerDetails = () => {
  const navigate = useNavigate();
  const { bunker_id } = useParams();
  const user = useUserData();
  const [loader, setLoader] = useState(false);
  const [staffDashboard, setStaffDashboard] = useState("");
  const [subTankList, setSubTankList] = useState([]);
  const [submitStatus, setSubmitStatus] = useState(false);
  const [softDelete, setSoftDelete] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [errors, setErrors] = useState({});

  // ✅ initialize with tanks array
  const [form, setForm] = useState({
    bunkerDate: moment().format("YYYY-MM-DD"),
    bdnReferenceNo: "",
    bunkeringLocation: "",
    supplierName: "",
    chartererOrOwner: "",
    bargeNameAndImo: "",
    grade: "",
    density: "",
    sulphur: "",
    // quantity: "",
    chiefEngSign: "",
    totalquantity: "",
    tanks: [{ tankId: null, quantity: "" }],
  });



  // generic change handler
  const handleChange = (key, value) => {
    // Prevent negative numbers for quantity and specific metrics
    if (["totalquantity", "sulphur", "density"].includes(key) && value < 0) {
      return;
    }
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" })); // Clear error when user types
  };

  // handle tanks
  const handleTankChange = (index, value) => {
    const updated = [...form.tanks];
    updated[index].tankId = value;
    setForm((prev) => ({ ...prev, tanks: updated }));
  };

  const handleQuantityChange = (index, value) => {
    if (value < 0) return; // Prevent negative numbers
    const updated = [...form.tanks];
    updated[index].quantity = value;
    setForm((prev) => ({ ...prev, tanks: updated }));
  };

  const handleAddTank = () => {
    setForm((prev) => ({
      ...prev,
      tanks: [...prev.tanks, { tankId: null, quantity: "" }],
    }));
  };

  // fetch tanks list
  const listSubTanks = async () => {
    const staffDetails = JSON.parse(localStorage.getItem("user_data"));
    try {
      const formData = new FormData();
      formData.append("ship_id", staffDetails?.data?.ship_details?.ship_id);
      const res = await post(ApiURL?.bunker_report_list, formData);
      if (res?.status === true) {
        setSubTankList(res?.tanks);
      }
    } catch (err) {
      console.error("Error fetching Sub Tanks list:", err);
    }
  };

  // update bunker details
  const updateBunkerDetails = async (save_submit) => {
    setLoader(true);
    try {
      const formData = new FormData();
      formData.append("request_type", "edit");
      formData.append("voyage_id", staffDashboard?.voyage_details?.[0]?.id);
      formData.append("record_id", atob(bunker_id));
      formData.append("save_submit", Number(save_submit) || 0);
      formData.append("bunker_date", form.bunkerDate);
      formData.append("bdn_reference_no", form.bdnReferenceNo);
      formData.append("bunkering_location", form.bunkeringLocation);
      formData.append("supplier_name", form.supplierName);
      formData.append("charterer_or_owner", form.chartererOrOwner);
      formData.append("barge_name_and_imo", form.bargeNameAndImo);
      formData.append("grade", form.grade);
      formData.append("density", form.density);
      formData.append("sulphur", form.sulphur);
      formData.append("chief_eng_sign", form.chiefEngSign);
      formData.append("totalquantity", form.totalquantity);
      formData.append("tanks", JSON.stringify(form.tanks));



      const res = await post(ApiURL.update_bunker_details_st_bd, formData);
      const responseData = res?.response?.data || res;

      if (responseData?.status === true) {
        SuccessToast("Success!", "Bunker details updated successfully");
        navigate("/list-bunker-detail");
      } else {
        let newErrors = responseData?.errors || {};
        // If error is returned as a direct string message for a specific field
        if (responseData?.error && typeof responseData.error === "string") {
          if (
            responseData.error.toLowerCase().includes("totalquantity") ||
            responseData.error.toLowerCase().includes("total quantity")
          ) {
            newErrors = { ...newErrors, totalquantity: responseData.error };
          } else if (responseData.error.toLowerCase().includes("sulphur")) {
            newErrors = { ...newErrors, sulphur: responseData.error };
          }
        }
        setErrors(newErrors);
        if (Object.keys(newErrors).length === 0) {
          const errorMessage =
            responseData?.message ||
            (typeof responseData?.error === "string" ? responseData.error : "Validation Failed");
          ErrorToast("Error", errorMessage);
        }
      }
    } catch (err) {
      console.error("Error while updating bunker details:", err);
    } finally {
      setLoader(false);
    }
  };

  // view bunker details
  const viewBunkerDetails = async (voyageData) => {
    try {
      const formData = new FormData();
      formData.append("voyage_id", voyageData?.voyage_details?.[0]?.id);
      formData.append("record_id", atob(bunker_id));
      formData.append("request_type", "view");

      const res = await post(ApiURL.update_bunker_details_st_bd, formData);
      if (res?.status === true) {
        const { data } = res;

        setForm({
          bunkerDate: data?.bunker_date || moment().format("YYYY-MM-DD"),
          bdnReferenceNo: data?.bdn_reference_no || "",
          bunkeringLocation: data?.bunkering_location || "",
          supplierName: data?.supplier_name || "",
          chartererOrOwner: data?.charterer_or_owner || "",
          bargeNameAndImo: data?.barge_name_and_imo || "",
          grade: data?.grade || "",
          density: data?.density || "",
          sulphur: data?.sulphur || "",
          chiefEngSign: data?.chief_eng_sign || "",
          totalquantity: data?.totalquantity || "",
          tanks: Array.isArray(data?.tanks)
            ? data.tanks
            : [{ tankId: null, quantity: "" }],
        });

        setSubmitStatus(data?.submit_status);
        setSoftDelete(data?.soft_delete);
      }
    } catch (err) {
      console.error("Error fetching bunker details:", err);
    }
  };

  useEffect(() => {
    const staffDashboards = localStorage.getItem("staff_dashboard");
    const parsedData = staffDashboards ? JSON.parse(staffDashboards) : null;

    if (parsedData) {
      setStaffDashboard(parsedData);
      viewBunkerDetails(parsedData);
      listSubTanks();
    }
  }, []);

  return (
    <>
      <VoyageDetails staff_dashboard={staffDashboard?.voyage_details} />
      <div className="white-container">
        <h3>Edit Bunker Record</h3>
        <br />

        {/* First row */}
        <div className="row">
          <div className="col-6">
            <AppInput
              disabled={submitStatus}
              type="date"
              label="Bunker Date"
              value={form.bunkerDate}
              onChange={(e) => handleChange("bunkerDate", e.target.value)}
            />
          </div>
          <div className="col-6">
            <AppInput
              disabled={submitStatus}
              label="BDN Reference No."
              value={form.bdnReferenceNo}
              onChange={(e) => handleChange("bdnReferenceNo", e.target.value)}
            />
          </div>
        </div>

        {/* Second row */}
        <div className="row">
          <div className="col-6">
            <AppInput
              disabled={submitStatus}
              label="Bunkering Location"
              value={form.bunkeringLocation}
              onChange={(e) =>
                handleChange("bunkeringLocation", e.target.value)
              }
            />
          </div>
          <div className="col-6">
            <AppInput
              disabled={submitStatus}
              label="Name of the Supplier"
              value={form.supplierName}
              onChange={(e) => handleChange("supplierName", e.target.value)}
            />
          </div>
        </div>

        {/* Third row */}
        <div className="row">
          <div className="col-6">
            <AppInput
              disabled={submitStatus}
              label="Bunker Supplied by Charterer/Owner"
              value={form.chartererOrOwner}
              onChange={(e) => handleChange("chartererOrOwner", e.target.value)}
            />
          </div>
          <div className="col-6">
            <AppInput
              disabled={submitStatus}
              label="Barge Name details / IMO No."
              value={form.bargeNameAndImo}
              onChange={(e) => handleChange("bargeNameAndImo", e.target.value)}
            />
          </div>
        </div>

        {/* Fourth row */}
        <div className="row">
          <div className="col-6">
            <label className="form-label">Grade</label>
            <Select
              disabled={submitStatus}
              value={form.grade}
              style={{ width: "100%" }}
              placeholder="Select Fuel Grade"
              options={fuelTypeOptions}
              onChange={(value) => handleChange("grade", value)}
            />
          </div>
          <div className="col-6">
            <AppInput
              disabled={submitStatus}
              label="Density at 15°C (Kg/m³)"
              value={form.density}
              onChange={(e) => handleChange("density", e.target.value)}
            />
          </div>
        </div>

        {/* Fifth row */}
        <div className="row">
          <div className="col-6">
            <AppInput
              disabled={submitStatus}
              label="Sulphur (%m/m)"
              value={form.sulphur}
              onChange={(e) => handleChange("sulphur", e.target.value)}
              errors={errors.sulphur}
            />
          </div>
          <div className="col-6">
            <AppInput
              type="number"
              min={0}
              label=" Total Quantity (MT)"
              value={form.totalquantity}
              onChange={(e) => handleChange("totalquantity", e.target.value)}
              errors={errors.totalquantity}
            />
          </div>
        </div>

        {/* Sixth row */}
        <div className="row">
          <div className="col-6">
            <AppInput
              disabled={submitStatus}
              label="Chief Eng. Name & Sign"
              value={form.chiefEngSign}
              onChange={(e) => handleChange("chiefEngSign", e.target.value)}
            />
          </div>
        </div>

        {/* Tanks Section */}
        <div className="mt-3 input-box p-3">
          {form.tanks.map((tank, index) => (
            <Row gutter={16} key={index} style={{ marginBottom: 16 }}>
              <Col span={12}>
                <label className="form-label">Tank No.</label>
                <Select
                  value={tank.tankId}
                  onChange={(value) => handleTankChange(index, value)}
                  placeholder="Select Tank"
                  style={{ width: "100%" }}
                >
                  {subTankList?.map((t) => (
                    <Option key={t.id} value={t.id}>
                      {`${t.id} - ${t.category_name} (${
                        t.fuel_type || "No fuel"
                      })`}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col span={12}>
                <label className="form-label">Quantity (MT)</label>
                <Input
                  type="number"
                  min={0}
                  value={tank.quantity}
                  onChange={(e) => handleQuantityChange(index, e.target.value)}
                  placeholder="Enter Quantity"
                />
              </Col>
            </Row>
          ))}

          <Button type="dashed" onClick={handleAddTank}>
            + Add Another Tank
          </Button>
        </div>

        {/* Submit button for Chief Engineer */}
        <div className="row">
          {!softDelete && user?.data?.designation === "Chief Engineer" && (
            <div
              className="input-box"
              style={{ marginLeft: "15px", marginTop: "15px" }}
            >
              <Button
                loading={loader}
                disabled={submitStatus}
                className="save_btn"
                style={{ color: "white" }}
                onClick={() => setModalVisible(true)}
              >
                Submit
              </Button>
            </div>
          )}
        </div>

        <SecurityCodeModal
          record_id={atob(bunker_id)}
          visible={modalVisible}
          setModalVisible={setModalVisible}
          onCancel={() => setModalVisible(false)}
          onSuccess={() => {
            updateBunkerDetails("1");
            setModalVisible(false);
          }}
          apiEndPoint={ApiURL.verify_security_code_st}
        />
      </div>
    </>
  );
};

export default EditBunkerDetails;
